<template>
    <ul
        v-if="pager.pages && pager.pages.length"
        class="pagination pagination-lg justify-content-center"
    >
        <li
            class="page-item previous"
            :class="{ disabled: pager.currentPage === 1 }"
        >
            <a
                class="page-link"
                :aria-label="labels.previous"
                @click="setPage(pager.currentPage - 1)"
            >
                <span aria-hidden="true">&laquo;</span>
            </a>
        </li>
        <li
            v-for="page in pager.pages"
            :key="page"
            class="page-item page-number"
            :class="{ active: pager.currentPage === page }"
        >
            <a class="page-link" @click="setPage(page)">
                {{ page }}
            </a>
        </li>
        <li
            class="page-item"
            :class="{ disabled: pager.currentPage === pager.totalPages }"
        >
            <a
                class="page-link"
                :aria-label="labels.next"
                @click="setPage(pager.currentPage + 1)"
            >
                <span aria-hidden="true">&raquo;</span>
            </a>
        </li>
    </ul>
</template>

<script>
const defaultLabels = {
    previous: 'Previous',
    next: 'Next',
};
export default {
    props: {
        items: {
            type: Array,
            required: true,
        },
        initialPage: {
            type: Number,
            default: 1,
        },
        pageSize: {
            type: Number,
            default: 5,
        },
        maxPages: {
            type: Number,
            default: 10,
        },
        labels: {
            type: Object,
            default: () => defaultLabels,
        },
    },
    data() {
        return {
            pager: {},
        };
    },
    created() {
        if (!this.$listeners.changePage) {
            throw 'Missing required event listener: "changePage"';
        }
        // set to initial page
        this.setPage(this.initialPage);
    },
    methods: {
        setPage(page) {
            const { items, pageSize, maxPages } = this;
            let totalItems = items.length;
            let currentPage = page;
            // get new pager object for specified page
            // calculate number of pages
            let totalPages = Math.ceil(totalItems / pageSize);
            // avoid overflow of the current page
            if (currentPage < 1) {
                currentPage = 1;
            } else if (currentPage > totalPages) {
                currentPage = totalPages;
            }

            let startPage, endPage;
            if (totalPages <= maxPages) {
                // show all pages
                startPage = 1;
                endPage = totalPages;
            } else {
                // compute start and end pages to max max size
                let maxPagesBeforeCurrentPage = Math.floor(maxPages / 2);
                let maxPagesAfterCurrentPage = Math.ceil(maxPages / 2) - 1;
                if (currentPage <= maxPagesBeforeCurrentPage) {
                    // current page near start
                    startPage = 1;
                    endPage = maxPages;
                } else if (
                    currentPage + maxPagesAfterCurrentPage >=
                    totalPages
                ) {
                    // current page near end
                    startPage = totalPages - maxPages + 1;
                    endPage = totalPages;
                } else {
                    // current page somewhere in middle
                    startPage = currentPage - maxPagesBeforeCurrentPage;
                    endPage = currentPage + maxPagesAfterCurrentPage;
                }
            }

            // compute start and end item indexes
            let startIndex = (currentPage - 1) * pageSize;
            let endIndex = Math.min(startIndex + pageSize - 1, totalItems - 1);
            // create an array of pages to repeat in the pager control
            let pages = Array.from(Array(endPage + 1 - startPage).keys()).map(
                (i) => startPage + i
            );
            // object with all pager properties required by the view
            const pager = {
                totalItems: totalItems,
                currentPage: currentPage,
                pageSize: pageSize,
                totalPages: totalPages,
                startPage: startPage,
                endPage: endPage,
                startIndex: startIndex,
                endIndex: endIndex,
                pages: pages,
            };

            // get new page of items from items array
            const pageOfItems = items.slice(
                pager.startIndex,
                pager.endIndex + 1
            );
            // update pager
            this.pager = pager;
            // emit change page event to parent component
            this.$emit('changePage', pageOfItems);
        },
    },
    watch: {
        items() {
            this.setPage(this.initialPage);
        },
    },
};
</script>
